<template>
  <!-- Floating pill pinned to the bottom-right of the viewport. Opens
       a YouTube lightbox on click instead of navigating away. -->
  <div v-if="shouldRender">
    <div class="v2-founder-cta">
      <div class="v2-founder-cta__inner">
        <button
          type="button"
          class="v2-founder-cta__btn"
          aria-haspopup="dialog"
          :aria-expanded="isOpen ? 'true' : 'false'"
          @click="open"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="v2-founder-cta__icon" aria-hidden="true">
            <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/>
            <rect x="2" y="6" width="14" height="12" rx="2"/>
          </svg>
          <span class="v2-founder-cta__label">{{ founderCta.label || 'A Word From Our Founder' }}</span>
        </button>
      </div>
    </div>

    <!-- Teleport the modal to <body> so z-index stacking is free of any
         parent transform/overflow traps. -->
    <Teleport to="body">
      <transition name="v2-founder-backdrop">
        <div
          v-if="isOpen"
          class="v2-founder-modal__backdrop"
          @click="close"
          aria-hidden="true"
        />
      </transition>

      <transition name="v2-founder-panel">
        <div
          v-if="isOpen"
          class="v2-founder-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="v2-founder-modal-title"
          @keydown.esc="close"
        >
          <div class="v2-founder-modal__panel" @click.stop>
            <!-- Editorial pink accent bar — the brand signature. -->
            <span class="v2-founder-modal__accent" aria-hidden="true" />

            <button
              ref="closeBtn"
              type="button"
              class="v2-founder-modal__close"
              aria-label="Close"
              @click="close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            <header class="v2-founder-modal__header">
              <span class="v2-founder-modal__eyebrow">
                <span class="v2-founder-modal__eyebrow-dot" aria-hidden="true" />
                {{ founderCta.label || 'A Word From Our Founder' }}
              </span>
              <h3 id="v2-founder-modal-title" class="v2-founder-modal__title">
                {{ modalTitle }}
              </h3>
            </header>

            <div class="v2-founder-modal__body">
              <!-- Video frame with brand-tinted glow. Uses a stacking
                   pattern: navy backdrop → thumbnail poster (fades out
                   when iframe mounts) → iframe above. -->
              <div class="v2-founder-modal__video">
                <iframe
                  v-if="embedUrl"
                  :src="embedUrl"
                  :title="founderCta.label || 'A Word From Our Founder'"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                  @load="onIframeLoad"
                ></iframe>
                <!-- Loading glyph sits underneath the iframe; iframe fade-in
                     reveals the video and covers this. -->
                <div v-if="embedUrl && !iframeReady" class="v2-founder-modal__video-loading" aria-hidden="true">
                  <span class="v2-founder-modal__video-spinner" />
                </div>
                <!-- Graceful fallback: non-YouTube link just opens in a new tab -->
                <a
                  v-if="!embedUrl && fallbackLink"
                  :href="fallbackLink"
                  target="_blank"
                  rel="noopener"
                  class="v2-founder-modal__video-fallback"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="v2-founder-modal__video-fallback-icon">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                  <span>View the full message</span>
                </a>
              </div>

              <p class="v2-founder-modal__desc">
                {{ modalDescription }}
              </p>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
// Link parsing lives in useYouTubeUrl rather than here; the parser this
// replaced captured the whole token (youtu.be/vHemPxwRwOwtrailing yielded a
// 19-character "id") and matched /embed/ on any host, so vimeo.com/embed/xyz
// came back as a YouTube embed. Both are pinned in V2FounderCta.spec.js.
//
// allowBareId is off because `founder_cta.video_url` is free text an admin
// types, not a url the system generated: "founder-msg" is 11 legal characters
// and would otherwise embed as a video id, suppressing the fallback link.
import {
  extractYoutubeID,
  youtubeEmbedUrl,
} from '../../../../composables/useYouTubeUrl'

export default {
  name: 'V2FounderCta',
  props: {
    founderCta: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isOpen: false,
      iframeReady: false,
      previouslyFocused: null,
    }
  },
  computed: {
    shouldRender() {
      return !!(
        this.founderCta
        && (this.founderCta.video_url || this.founderCta.link)
      )
    },
    // Prefer an explicit video_url field. A YouTube url in `link` is tolerated
    // rather than expected — `link` is normally the non-YouTube fallback page,
    // as the seeder ships it — but one put there is still honoured.
    videoId() {
      const opts = { allowBareId: false }
      return (
        extractYoutubeID(this.founderCta?.video_url, opts)
        || extractYoutubeID(this.founderCta?.link, opts)
      )
    },
    embedUrl() {
      if (!this.videoId) return null
      // autoplay=1 + rel=0 on modal open gives an immediate start without
      // autoplay triggering on the page itself (iframe mounted conditionally).
      // modestbranding=1 removes the YouTube logo in the control bar.
      return `${youtubeEmbedUrl(this.videoId)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
    },
    // The visitor's way through when we cannot embed. `link` is the authored
    // fallback page; `video_url` backs it up so a real-but-unembeddable video
    // (Vimeo, a Drive share, a nocookie link) stays reachable.
    //
    // Without that second branch the modal has a hole: shouldRender accepts
    // `video_url || link`, but the two render branches are `embedUrl` and
    // `!embedUrl && fallbackLink`. An unparseable video_url with no link
    // satisfies the outer gate and neither inner one, so the pill renders on
    // every page and the modal opens onto an empty frame — no iframe, no
    // spinner, no link, and nothing logged.
    //
    // Guarded to absolute http(s): an unparseable scrap of text in this field
    // would otherwise become <a href="some-text">, a same-origin navigation
    // that walks the visitor off the campaign page. Rendering nothing beats
    // sending them somewhere wrong.
    fallbackLink() {
      const link = this.founderCta?.link
      if (link) return link
      const videoUrl = this.founderCta?.video_url
      return /^https?:\/\//i.test(String(videoUrl || '')) ? videoUrl : null
    },
    modalTitle() {
      return this.founderCta?.modal_title || 'Why This Fight Matters'
    },
    modalDescription() {
      return (
        this.founderCta?.modal_description
        || 'Hear directly from our founder about the mission driving Side-Out Foundation and why your support makes all the difference.'
      )
    },
  },
  methods: {
    open() {
      this.previouslyFocused = document.activeElement
      this.iframeReady = false
      this.isOpen = true
      document.body.style.overflow = 'hidden'
      this.$nextTick(() => {
        if (this.$refs.closeBtn && this.$refs.closeBtn.focus) {
          this.$refs.closeBtn.focus({ preventScroll: true })
        }
      })
      document.addEventListener('keydown', this._onEsc)
    },
    close() {
      this.isOpen = false
      this.iframeReady = false
      document.body.style.overflow = ''
      document.removeEventListener('keydown', this._onEsc)
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus()
      }
    },
    onIframeLoad() {
      this.iframeReady = true
    },
    _onEsc(e) {
      if (e.key === 'Escape') this.close()
    },
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this._onEsc)
    document.body.style.overflow = ''
  },
}
</script>

<style scoped>
/* ─── Floating trigger pill (unchanged visual) ─────────────── */
.v2-founder-cta {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 24px;
  /* Must sit below the donation-widget modal backdrop (z-20) so the
     floating pill doesn't show through the overlay when the donate
     flow is open. Still above normal page content (z-auto). */
  z-index: 15;
  pointer-events: none;
}
.v2-founder-cta__inner {
  max-width: 1400px;
  margin-inline: auto;
  padding-inline: 24px;
  display: flex;
  justify-content: flex-end;
}
/* Matches lg:px-16 on V2Hero/V2Footer; scoped CSS here because this container doesn't use Tailwind utilities. */
@media (min-width: 768px)  { .v2-founder-cta__inner { padding-inline: 48px; } }
@media (min-width: 1024px) { .v2-founder-cta__inner { padding-inline: 64px; } }
.v2-founder-cta__btn {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: #009cde;
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1),
              filter 200ms;
  font-family: inherit;
}
.v2-founder-cta__btn:hover {
  transform: scale(1.05);
  filter: brightness(1.05);
}
.v2-founder-cta__btn:active { transform: scale(1.01); }
.v2-founder-cta__btn:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
  box-shadow: 0 0 0 4px #009cde;
}
.v2-founder-cta__icon { width: 16px; height: 16px; flex-shrink: 0; stroke: currentColor; }
.v2-founder-cta__label {
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 20px;
  white-space: nowrap;
}

@media (max-width: 640px) {
  /* Hidden on mobile per client direction — the pill duplicates a CTA
     that already lives in the V2About founder card on small screens
     and crowds the sticky donate bar. Desktop placement unchanged. */
  .v2-founder-cta { display: none; }
}

/* ─── Modal: backdrop ─────────────────────────────────────── */
/* Radial-vignette backdrop: darkens near the panel for cinematic
   focus while still keeping the page legible at the edges. */
.v2-founder-modal__backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  background:
    radial-gradient(ellipse at center,
      rgba(29, 37, 73, 0.58) 0%,
      rgba(10, 13, 28, 0.78) 65%,
      rgba(10, 13, 28, 0.85) 100%);
  -webkit-backdrop-filter: blur(10px) saturate(140%);
  backdrop-filter: blur(10px) saturate(140%);
}

/* ─── Modal: layout ───────────────────────────────────────── */
.v2-founder-modal {
  position: fixed;
  inset: 0;
  z-index: 51;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(16px, 4vw, 48px);
  pointer-events: none;
}
.v2-founder-modal__panel {
  pointer-events: auto;
  position: relative;
  width: 100%;
  max-width: 44rem;
  background:
    linear-gradient(180deg, #ffffff 0%, #fefafc 100%);
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: min(90vh, 900px);
  font-family: 'Red Hat Display', sans-serif;
}

/* Decorative pink accent bar at top of panel — editorial signature. */
.v2-founder-modal__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg,
    transparent 0%,
    var(--sideout-pink) 20%,
    #ff4fa5 50%,
    var(--sideout-pink) 80%,
    transparent 100%);
  pointer-events: none;
}

/* Close button — absolute top-right, refined pill that inverts on hover. */
.v2-founder-modal__close {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 2;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(29, 37, 73, 0.08);
  border-radius: 9999px;
  color: var(--sideout-navy);
  cursor: pointer;
  transition:
    background 220ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 220ms cubic-bezier(0.4, 0, 0.2, 1),
    color 220ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.v2-founder-modal__close svg {
  width: 16px;
  height: 16px;
  stroke: currentColor;
  transition: transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
}
.v2-founder-modal__close:hover {
  background: var(--sideout-pink);
  border-color: var(--sideout-pink);
  color: #ffffff;
  transform: scale(1.06);
}
.v2-founder-modal__close:hover svg { transform: rotate(90deg); }
.v2-founder-modal__close:focus-visible {
  outline: 2px solid var(--sideout-pink);
  outline-offset: 3px;
}

/* Header — centered editorial block above the video. */
.v2-founder-modal__header {
  padding: 44px 48px 20px;
  text-align: center;
}
.v2-founder-modal__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--sideout-pink);
  font-family: 'Red Hat Display', sans-serif;
}
.v2-founder-modal__eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: var(--sideout-pink);
}
.v2-founder-modal__title {
  margin: 14px 0 0;
  font-size: clamp(24px, 3.2vw, 32px);
  font-weight: 900;
  line-height: 1.08;
  color: var(--sideout-navy);
  letter-spacing: -0.015em;
  text-wrap: balance;
}

/* Body — video + description. */
.v2-founder-modal__body {
  padding: 0 48px 44px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
}

/* Video frame. */
.v2-founder-modal__video {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 18px;
  overflow: hidden;
  background: var(--sideout-navy);
}
.v2-founder-modal__video iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  opacity: 0;
  transition: opacity 420ms cubic-bezier(0.4, 0, 0.2, 1);
}
/* Iframe starts hidden and fades in once loaded. This prevents the
   single-frame flash where the YouTube player shows a stale black
   frame before first-frame decode. */
.v2-founder-modal__video iframe[src] { opacity: 1; }

/* Spinner shown underneath the iframe while it loads. */
.v2-founder-modal__video-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at center,
      rgba(218, 24, 132, 0.15) 0%,
      rgba(29, 37, 73, 0) 60%),
    var(--sideout-navy);
  pointer-events: none;
}
.v2-founder-modal__video-spinner {
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: var(--sideout-pink);
  animation: v2-founder-spin 0.9s linear infinite;
}
@keyframes v2-founder-spin {
  to { transform: rotate(360deg); }
}

/* Fallback when the link isn't a YouTube URL. */
.v2-founder-modal__video-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #ffffff;
  text-decoration: none;
  background:
    radial-gradient(circle at center,
      rgba(218, 24, 132, 0.25) 0%,
      rgba(29, 37, 73, 0) 65%),
    var(--sideout-navy);
  transition: background 220ms;
}
.v2-founder-modal__video-fallback:hover {
  background:
    radial-gradient(circle at center,
      rgba(218, 24, 132, 0.4) 0%,
      rgba(29, 37, 73, 0) 70%),
    var(--sideout-navy);
}
.v2-founder-modal__video-fallback-icon {
  width: 28px;
  height: 28px;
  stroke: currentColor;
}
.v2-founder-modal__video-fallback span {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-family: 'Red Hat Display', sans-serif;
}

/* Description — reading-width, soft muted tone. */
.v2-founder-modal__desc {
  margin: 0 auto;
  max-width: 38rem;
  font-size: 15px;
  line-height: 1.65;
  color: var(--sideout-muted);
  text-align: center;
  font-family: 'Red Hat Text', 'Red Hat Display', sans-serif;
}

/* ─── Mobile refinement ───────────────────────────────────── */
@media (max-width: 640px) {
  .v2-founder-modal__header { padding: 36px 24px 16px; }
  .v2-founder-modal__body { padding: 0 24px 28px; gap: 18px; }
  .v2-founder-modal__close { top: 12px; right: 12px; width: 36px; height: 36px; }
  .v2-founder-modal__title { font-size: 22px; }
  .v2-founder-modal__desc { font-size: 14px; }
  .v2-founder-modal__panel { border-radius: 20px; }
}

/* ─── Transitions ─────────────────────────────────────────── */
/* Backdrop fades first, slightly longer than before for a more
   cinematic dim-to-black feel. */
.v2-founder-backdrop-enter-active,
.v2-founder-backdrop-leave-active {
  transition: opacity 280ms cubic-bezier(0.4, 0, 0.2, 1);
}
.v2-founder-backdrop-enter-from,
.v2-founder-backdrop-leave-to { opacity: 0; }

/* Panel enters with a 60ms delay (backdrop settles first), then
   does a spring scale+translate with micro-overshoot. */
.v2-founder-panel-enter-active { transition: opacity 480ms; }
.v2-founder-panel-enter-active .v2-founder-modal__panel {
  animation: v2-founder-panel-in 560ms cubic-bezier(0.22, 1, 0.36, 1) 60ms both;
}
.v2-founder-panel-leave-active { transition: opacity 220ms; }
.v2-founder-panel-leave-active .v2-founder-modal__panel {
  animation: v2-founder-panel-out 260ms cubic-bezier(0.4, 0, 0.6, 1) both;
}
.v2-founder-panel-enter-from, .v2-founder-panel-leave-to { opacity: 0; }

@keyframes v2-founder-panel-in {
  0%   { opacity: 0; transform: translate3d(0, 24px, 0) scale(0.92); }
  60%  { opacity: 1; transform: translate3d(0, -2px, 0) scale(1.012); }
  100% { opacity: 1; transform: translate3d(0, 0, 0)    scale(1); }
}
@keyframes v2-founder-panel-out {
  from { opacity: 1; transform: translate3d(0, 0, 0)    scale(1); }
  to   { opacity: 0; transform: translate3d(0, 16px, 0) scale(0.96); }
}

@media (prefers-reduced-motion: reduce) {
  .v2-founder-panel-enter-active .v2-founder-modal__panel,
  .v2-founder-panel-leave-active .v2-founder-modal__panel {
    animation-duration: 140ms !important;
    animation-timing-function: ease-out !important;
    animation-delay: 0ms !important;
  }
  .v2-founder-backdrop-enter-active,
  .v2-founder-backdrop-leave-active { transition-duration: 100ms; }
  .v2-founder-modal__video iframe { transition: none; }
  .v2-founder-modal__video-spinner { animation-duration: 1.8s; }
}
</style>
